import { useAtom } from '@reatom/npm-react'
import { motion } from 'motion/react'
import styled, { css } from 'styled-components'

import { Typography, useHapticFeedback } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { useVisibilityFormatter } from '@root/entities/preferences'
import { SummaryChartMiniature } from '@root/entities/summary/ui/summary-chart/mobile/summary-chart-miniature'
import { AmountFormatted } from '@root/shared/core/format'
import { ThreeDotsVerticalIcon } from '@root/shared/ui-kit'

import { WalletAddressTruncated } from '../../common/wallet-address-truncated'
import { FavoriteWalletStar } from '../../favorite-wallet-star/favorite-wallet-star'
import { WalletModelShort } from '../../wallet-short'

import type { WalletModel } from '@root/entities/wallet/atoms/reatom-wallet'

export const COLLAPSED_WALLET_ITEM_HEIGHT = 60

export type Props = {
  wallet: WalletModel
  variant: 'default' | 'radio'
  onDetailsClick?: React.MouseEventHandler<HTMLElement>
  selected?: boolean
  onSelected?: () => void
  onArrowClick?: React.MouseEventHandler<HTMLElement>
  renderFooter?: () => React.ReactNode
}

export const WalletListItemCard: React.FC<Props> = ({
  wallet,
  onDetailsClick,
  onArrowClick,
  renderFooter,
}) => {
  const formatHidden = useVisibilityFormatter('****')
  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)
  const haptics = useHapticFeedback()

  const [walletBalanceNative] = useAtom(wallet.totalBalanceNative)
  const [walletBalanceUsd] = useAtom(wallet.totalBalanceUsd)

  const handleDetailsClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (onDetailsClick) {
      haptics.impactOccurred('light')
      onDetailsClick(e)
    }
  }

  const balanceFormatted = formatHidden(
    <>
      <AmountFormatted amount={walletBalanceNative} roundingMode="trunc" />
      <span> {nativeCurrency.ticker}</span>
    </>,
  )

  return (
    <CollapsedContent layout onClick={onArrowClick}>
      <TitleContainer>
        <WalletName wallet={wallet} size="xs" />
        <Footer>
          <WalletAddress walletAddress={wallet.address} withCopy />
          {renderFooter?.()}
        </Footer>
      </TitleContainer>
      <ChartContainer>
        <Chart wallet={wallet} />
      </ChartContainer>
      <BalanceContainer>
        <Balance variant="body4-bold">{balanceFormatted}</Balance>
        <BalanceAlternate variant="body5-semi-bold">
          {formatHidden(
            <>
              $
              <AmountFormatted amount={walletBalanceUsd} roundingMode="trunc" />
            </>,
          )}
        </BalanceAlternate>
      </BalanceContainer>

      {onDetailsClick ? (
        <IconWrap
          css={css`
            grid-column: settings;
            cursor: pointer;
          `}
          onClick={handleDetailsClick}
        >
          <ThreeDotsVerticalIcon />
        </IconWrap>
      ) : undefined}
      <StarContainer>
        <Star wallet={wallet} size={16} />
      </StarContainer>
    </CollapsedContent>
  )
}

const CollapsedContent = styled(motion.div)`
  display: grid;
  grid-auto-flow: column;

  grid-template-columns:
    [content] 1fr [chart] 1fr
    [balances] 0.7fr [settings] auto [favorite] auto;

  align-items: center;
  height: ${COLLAPSED_WALLET_ITEM_HEIGHT}px;

  svg {
    color: var(--text-7);
  }
`

const TitleContainer = styled.div`
  grid-column: content;
  grid-column: content;
`

const Balance = styled(Typography)`
  overflow: hidden;
  color: var(--text-12);
  text-align: right;
  white-space: nowrap;
`
const BalanceAlternate = styled(Typography)`
  color: var(--text-8);
  text-align: right;
`

const BalanceContainer = styled.div`
  grid-column: balances;
  display: grid;
  justify-items: end;
  grid-template-rows: auto auto;
  margin-right: 6px;
`

const WalletName = styled(WalletModelShort)`
  overflow: hidden;
  color: var(--text-12);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
`

const WalletAddress = styled(WalletAddressTruncated)`
  color: var(--text-8);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  gap: 4px;
`

const Footer = styled.div`
  color: var(--text-8);
  display: grid;
  justify-content: start;
  align-items: center;
  grid-auto-flow: column;
  gap: 4px;
`

const IconWrap = styled.div`
  color: var(--text-8);
  display: grid;
`

const Star = styled(FavoriteWalletStar)``

const StarContainer = styled(IconWrap)`
  grid-column: favorite;
  cursor: pointer;
`

const Chart = styled(SummaryChartMiniature)`
  width: 75px;
  height: 30px;
`

const ChartContainer = styled.div`
  grid-column: chart;
  display: grid;
  justify-content: center;
`
